import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSafeBack } from "../hooks/useSafeBack";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CalendarClock, Save } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/**
 * InstructorWeeklyReviewPrefs — choose preferred review day/time.
 * The dashboard widget reads these and shows a "Reserve slot" nudge
 * the day before each review.
 */
export default function InstructorWeeklyReviewPrefs() {
  const nav = useNavigate();
  const goBack = useSafeBack();
  const [day, setDay] = useState(6); // Sun
  const [time, setTime] = useState("18:00");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isPremium, setIsPremium] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/checks/preferences`, { withCredentials: true });
        if (cancelled) return;
        setDay(data.preferences?.weekly_review_day ?? 6);
        setTime(data.preferences?.weekly_review_time ?? "18:00");
        setIsPremium(!!data.is_premium);
      } catch {
        /* default values */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/checks/preferences`,
        { weekly_review_day: day, weekly_review_time: time },
        { withCredentials: true }
      );
      toast.success("Saved");
      goBack();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-dvh no-overflow-x flex items-center justify-center bg-slate-950"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-950 text-white pb-24">
      <header className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="prefs-back" onClick={() => goBack()} className="p-2 rounded-xl hover:bg-slate-800">
            <ArrowLeft className="w-5 h-5 text-slate-400" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-violet-400">Weekly review</p>
            <p className="text-[11px] text-slate-400 truncate">Pick the moment you'll always show up for your business.</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
        <div className="rounded-2xl bg-gradient-to-br from-violet-700 to-fuchsia-700 p-5">
          <CalendarClock className="w-5 h-5 text-white/80" />
          <h1 className="mt-2 text-xl font-extrabold">Reserve your weekly review</h1>
          <p className="text-[12px] text-white/80 mt-1 leading-snug">
            One protected slot a week to look at your numbers, plan ahead, and stay sharp.
            We'll remind you the day before and block the slot in your diary on request.
          </p>
        </div>

        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-4">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 mb-2">Day</p>
          <div className="grid grid-cols-7 gap-1">
            {DAYS.map((d, i) => (
              <button
                key={d}
                data-testid={`prefs-day-${i}`}
                onClick={() => setDay(i)}
                className={`py-2 rounded-xl text-[11px] font-extrabold transition ${
                  day === i
                    ? "bg-violet-600 text-white"
                    : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                }`}
              >
                {d}
              </button>
            ))}
          </div>

          <p className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 mt-4 mb-2">Time</p>
          <input
            data-testid="prefs-time"
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-white text-base focus:border-violet-500 outline-none"
          />
        </div>

        {!isPremium && (
          <div className="rounded-2xl bg-amber-500/15 border border-amber-500/40 p-3">
            <p className="text-[12px] text-amber-200 font-bold">
              Auto-reminders + slot booking unlock on Premium. You can still run reviews manually on Basic.
            </p>
          </div>
        )}

        <button
          data-testid="prefs-save"
          onClick={save}
          disabled={saving}
          className="w-full py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save preference
        </button>
      </div>
    </div>
  );
}
